import React, { useState, useEffect } from 'react';
import { X, DollarSign, Clock, Calendar, Users, Save, RotateCcw, Settings, Info } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const SettingsPanel = ({ isOpen, onClose, config, onConfigChange }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [localConfig, setLocalConfig] = useState({
    costPerHour: 160,
    comMeetingCost: 1000,
    agmMeetingCost: 2500,
    specialMeetingCost: 1000,
    annualizationMultiplier: 3,
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize local config from props
  useEffect(() => {
    if (config) {
      setLocalConfig({
        costPerHour: config.costPerHour || 160,
        comMeetingCost: config.comMeetingCost || 1000,
        agmMeetingCost: config.agmMeetingCost || 2500,
        specialMeetingCost: config.specialMeetingCost || 1000,
        annualizationMultiplier: config.annualizationMultiplier || 3,
      });
    }
  }, [config]);

  // Track changes
  useEffect(() => {
    if (config) {
      const changed = 
        localConfig.costPerHour !== (config.costPerHour || 160) ||
        localConfig.comMeetingCost !== (config.comMeetingCost || 1000) ||
        localConfig.agmMeetingCost !== (config.agmMeetingCost || 2500) ||
        localConfig.specialMeetingCost !== (config.specialMeetingCost || 1000) ||
        localConfig.annualizationMultiplier !== (config.annualizationMultiplier || 3);
      setHasChanges(changed);
    }
  }, [localConfig, config]);

  const handleChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setLocalConfig(prev => ({ ...prev, [field]: numValue }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onConfigChange(localConfig);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save config:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalConfig({
      costPerHour: 160,
      comMeetingCost: 1000,
      agmMeetingCost: 2500,
      specialMeetingCost: 1000,
      annualizationMultiplier: 3,
    });
  };

  if (!isOpen) return null;

  const InputField = ({ label, value, onChange, icon: Icon, suffix, description }) => (
    <div className="space-y-1.5">
      <label className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
        <Icon className="w-4 h-4 text-sky-500" />
        {label}
      </label>
      <div className="relative">
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>$</span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full pl-7 pr-14 py-2.5 rounded-lg border text-sm transition-colors
            ${isDark 
              ? 'bg-white/5 border-white/10 text-white focus:border-sky-500 focus:ring-1 focus:ring-sky-500' 
              : 'bg-white border-slate-300 text-slate-900 focus:border-sky-500 focus:ring-1 focus:ring-sky-500'}`}
        />
        {suffix && (
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {suffix}
          </span>
        )}
      </div>
      {description && (
        <p className={`text-xs flex items-start gap-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          {description}
        </p>
      )}
    </div>
  );

  const MultiplierField = ({ label, value, onChange, description }) => (
    <div className="space-y-1.5">
      <label className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
        <Clock className="w-4 h-4 text-purple-500" />
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          step="0.5"
          min="1"
          className={`w-full px-3 py-2.5 rounded-lg border text-sm transition-colors
            ${isDark 
              ? 'bg-white/5 border-white/10 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500' 
              : 'bg-white border-slate-300 text-slate-900 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'}`}
        />
        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          x multiplier
        </span>
      </div>
      {description && (
        <p className={`text-xs flex items-start gap-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          {description}
        </p>
      )}
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-md z-50 overflow-y-auto transition-transform
        ${isDark ? 'bg-[#111113]' : 'bg-white'}`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between
          ${isDark ? 'bg-[#111113] border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-sky-500/10' : 'bg-sky-50'}`}>
              <Settings className="w-5 h-5 text-sky-500" />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Cost Settings</h2>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Configure workload cost calculations</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
          >
            <X className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Labor Costs Section */}
          <div>
            <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2
              ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <DollarSign className="w-4 h-4" />
              Labor Costs
            </h3>
            <div className="space-y-4">
              <InputField
                label="Hourly Rate"
                value={localConfig.costPerHour}
                onChange={(v) => handleChange('costPerHour', v)}
                icon={Clock}
                suffix="per hour"
                description="Base hourly rate for calculating email and handle time costs"
              />
            </div>
          </div>

          {/* Meeting Costs Section */}
          <div>
            <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2
              ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Calendar className="w-4 h-4" />
              Meeting Costs
            </h3>
            <div className="space-y-4">
              <InputField
                label="COM Meeting Cost"
                value={localConfig.comMeetingCost}
                onChange={(v) => handleChange('comMeetingCost', v)}
                icon={Users}
                suffix="per meeting"
                description="Cost per Committee meeting (includes preparation and follow-up)"
              />
              <InputField
                label="AGM Meeting Cost"
                value={localConfig.agmMeetingCost}
                onChange={(v) => handleChange('agmMeetingCost', v)}
                icon={Users}
                suffix="per meeting"
                description="Cost per Annual General Meeting (higher due to complexity)"
              />
              <InputField
                label="Special Meeting Cost"
                value={localConfig.specialMeetingCost}
                onChange={(v) => handleChange('specialMeetingCost', v)}
                icon={Users}
                suffix="per meeting"
                description="Cost per Special/Extraordinary meeting"
              />
            </div>
          </div>

          {/* Annualization Section */}
          <div>
            <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2
              ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Clock className="w-4 h-4" />
              Annualization
            </h3>
            <div className="space-y-4">
              <MultiplierField
                label="Annualization Multiplier"
                value={localConfig.annualizationMultiplier}
                onChange={(v) => handleChange('annualizationMultiplier', v)}
                description="Multiplier to convert partial year data to annual estimates (e.g., 3x for 4 months of data)"
              />
            </div>
          </div>

          {/* Summary Card */}
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
            <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Current Configuration Summary</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Hourly Rate</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>${localConfig.costPerHour}/hr</p>
              </div>
              <div>
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Annualization</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{localConfig.annualizationMultiplier}x</p>
              </div>
              <div>
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>COM Meeting</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>${localConfig.comMeetingCost}</p>
              </div>
              <div>
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>AGM Meeting</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>${localConfig.agmMeetingCost}</p>
              </div>
              <div>
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Special Meeting</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>${localConfig.specialMeetingCost}</p>
              </div>
              <div>
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Est. Per Email</p>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>~${(localConfig.costPerHour / 6).toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 px-6 py-4 border-t flex items-center justify-between gap-3
          ${isDark ? 'bg-[#111113] border-white/10' : 'bg-white border-slate-200'}`}>
          <button
            onClick={handleReset}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${isDark 
                ? 'text-slate-400 hover:bg-white/5' 
                : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${isDark 
                  ? 'text-slate-400 hover:bg-white/5' 
                  : 'text-slate-600 hover:bg-slate-100'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all
                ${hasChanges 
                  ? 'bg-sky-500 hover:bg-sky-600 text-white' 
                  : isDark 
                    ? 'bg-white/5 text-slate-500 cursor-not-allowed' 
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;
